import { createColumnHelper } from '@tanstack/react-table';
import { StatusPill } from 'component-library';
import { useContext } from 'react';
import { AppContext } from '../contexts/AppContext';
import { BoatModel, Oem, Package, System } from '../models/ApiModels';
import { GetLicenseColor, GetStatusColor, GetStatusText } from './Custom';
import { ListFilterOption } from './UseListViewFilters';

export type SystemsListProps = {
  oemName?: string;
  bcOrder: string;
  humphreeId?: string;
  software: string;
  status: string;
  boatModel?: string;
  hullNumber?: string;
  serialNumber?: number;
  oemId?: string;
};

export type SystemColumnFilters = {
  oemName: string[];
  boatModel: string[];
  software: string[];
  status: string[];
};

export type SystemColumnFilterKey = keyof SystemColumnFilters;

type SystemFilterColumnDefinition = {
  key: SystemColumnFilterKey;
  label: string;
  searchable?: boolean;
};

export type SystemFilterOptionsByColumn = Record<SystemColumnFilterKey, ListFilterOption[]>;

export const systemDefaultColumnFilters: SystemColumnFilters = {
  oemName: [],
  boatModel: [],
  software: [],
  status: [],
};

export const systemDefaultFilterSearch: Partial<Record<SystemColumnFilterKey, string>> = {
  oemName: '',
  boatModel: '',
};

const systemFilterColumns: SystemFilterColumnDefinition[] = [
  { key: 'oemName', label: 'Owner', searchable: true },
  { key: 'boatModel', label: 'Boat model', searchable: true },
  { key: 'software', label: 'License' },
  { key: 'status', label: 'Status' },
];

function normalizeSearchValue(value: string) {
  return value.trim().toLowerCase();
}

function addSearchableValue(values: string[], value: unknown, visited: Set<unknown>) {
  if (value === null || value === undefined) return;

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    values.push(String(value));
    return;
  }

  if (value instanceof Date) {
    values.push(value.toISOString());
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((arrayValue) => addSearchableValue(values, arrayValue, visited));
    return;
  }

  if (typeof value === 'object') {
    if (visited.has(value)) return;
    visited.add(value);
    Object.values(value).forEach((objectValue) => addSearchableValue(values, objectValue, visited));
  }
}

function buildSystemSearchText(
  system: System,
  oemNameById: Map<string, string>,
  packageNameById: Map<string, string>,
  boatModelById: Map<string, BoatModel>
) {
  const values: string[] = [];
  const visited = new Set<unknown>();

  addSearchableValue(values, system, visited);

  const oemName = oemNameById.get(system.oemId);
  if (oemName) values.push(oemName);

  const packageName = packageNameById.get(system.packageId);
  if (packageName) values.push(packageName);

  const boatModel = boatModelById.get(system.boatModelId);
  if (boatModel) {
    values.push(boatModel.make);
    values.push(boatModel.name);
    values.push(`${boatModel.make} ${boatModel.name}`.trim());
  }

  return values.join(' ').toLowerCase();
}

export function FilterSystemsBySearch(
  systems: System[],
  searchValue: string,
  oems: Oem[],
  packages: Package[],
  boatModels: BoatModel[]
) {
  const query = normalizeSearchValue(searchValue);
  if (!query) return systems;

  const oemNameById = new Map<string, string>();
  const packageNameById = new Map<string, string>();
  const boatModelById = new Map<string, BoatModel>();

  oems.forEach((oem) => oemNameById.set(oem.id, oem.name));
  packages.forEach((packageItem) => {
    if (packageItem.id) packageNameById.set(packageItem.id, packageItem.displayName?.toString() || '');
  });
  boatModels.forEach((boatModel) => boatModelById.set(boatModel.id, boatModel));

  return systems.filter((system) => buildSystemSearchText(system, oemNameById, packageNameById, boatModelById).includes(query));
}

export function GetSystemListData(
  systems: System[],
  oems: Oem[],
  packages: Package[],
  boatModels?: BoatModel[],
  toggleInternal?: boolean
) {
  const { isHumphreeUser, currentOem } = useContext(AppContext);
  const systemList: SystemsListProps[] = systems.map((system: System) => {
    const boatMake = boatModels?.find((boatModel: BoatModel) => boatModel.id === system.boatModelId)?.make || '';
    const boatName = boatModels?.find((boatModel: BoatModel) => boatModel.id === system.boatModelId)?.name || '';

    return {
      oemId: system.oemId,
      oemName: oems.find((oem: Oem) => oem.id === system.oemId)?.name || (currentOem && currentOem.name),
      bcOrder: '1X23218',
      humphreeId: system.id,
      software:
        packages.find((packageItem: Package) => packageItem.id === system.packageId)?.displayName?.toString() || ' ',
      status: system.status,
      boatModel: boatMake + ' ' + boatName,
      hullNumber: system.hullNumber,
      serialNumber: system.controlUnitSerialNumber,
    };
  });

  //Hide internal (OEM Humphree Internal in PROD)
  const systemListNoInternal = systemList.filter(
    (system: any) => system.oemId !== 'd351d5d3-e474-47eb-b079-70c32f15683f'
  );

  //Remove retired and deleted for Oems
  const systemListHideForOem = systemList.filter(
    (system: any) => system.status !== 'Retired' && system.status !== 'Deleted'
  );
  if (!isHumphreeUser) return systemListHideForOem;
  else if (toggleInternal) return systemListNoInternal;
  else return systemList;
}

export function getSystemFilterColumnScrollHeightClass(key: SystemColumnFilterKey) {
  if (key === 'software' || key === 'status') return 'max-h-64';
  return 'max-h-52';
}

export function getSystemFilterValueForColumn(system: SystemsListProps, key: SystemColumnFilterKey): string {
  if (key === 'software') return system.software?.trim() || 'No license';
  if (key === 'status') return GetStatusText(system.status) || system.status;
  return `${system[key] ?? ''}`.trim();
}

export function buildSystemFilterOptionsByColumn(
  systems: SystemsListProps[],
  buildFilterOptions: (values: (string | number | undefined)[]) => ListFilterOption[]
): SystemFilterOptionsByColumn {
  return {
    oemName: buildFilterOptions(systems.map((system) => system.oemName)),
    boatModel: buildFilterOptions(systems.map((system) => system.boatModel)),
    software: buildFilterOptions(systems.map((system) => system.software?.trim() || 'No license')),
    status: buildFilterOptions(systems.map((system) => GetStatusText(system.status) || system.status)),
  };
}

export function buildSystemFilterPanelColumns(filterOptionsByColumn: SystemFilterOptionsByColumn) {
  return systemFilterColumns.map((column) => ({
    key: column.key,
    label: column.label,
    options: filterOptionsByColumn[column.key],
    searchable: column.searchable,
    scrollClassName: getSystemFilterColumnScrollHeightClass(column.key),
  }));
}

const columnHelper = createColumnHelper<SystemsListProps>();

export const systemListColumns = [
  columnHelper.accessor('humphreeId', {
    header: 'Humphree-ID',
    cell: (info) => <div className='truncate'>{info.getValue()}</div>,
    enableGlobalFilter: false,
    size: 100,
  }),
  columnHelper.accessor('serialNumber', {
    header: 'S/N',
    cell: (info) => <div className='truncate font-code prose-dataBaseDataLabel'>{info.getValue()}</div>,
    size: 100,
  }),
  columnHelper.accessor('oemName', {
    header: 'Owner',
    cell: (info) => <div className='truncate'>{info.getValue()}</div>,
  }),
  columnHelper.accessor('boatModel', {
    header: 'Boat model',
    cell: (info) => {
      return (
        <div className={`truncate ${info.getValue() === ' ' && 'italic text-gray-40 pr-1'}`}>
          {info.getValue() === ' ' ? 'Not assigned' : info.getValue()}
        </div>
      );
    },
    sortingFn: (rowA: any, rowB: any, columnId: any) => {
      const valueA = rowA.original[columnId];
      const valueB = rowB.original[columnId];

      // Handle undefined values
      if (valueA === undefined && valueB === undefined) return 0;
      if (valueA === undefined) return 1;
      if (valueB === undefined) return -1;

      // Your custom sorting logic for defined values
      return valueA.localeCompare(valueB);
    },
  }),
  columnHelper.accessor('hullNumber', {
    header: 'Hull number',
    cell: (info) => {
      return (
        <div className={`truncate ${!info.getValue() && 'italic text-gray-40 pr-1'}`}>
          {info.getValue() || 'Not assigned'}
        </div>
      );
    },
    sortingFn: (rowA: any, rowB: any, columnId: any) => {
      const valueA = rowA.original[columnId];
      const valueB = rowB.original[columnId];

      // Handle undefined values
      if (valueA === undefined && valueB === undefined) return 0;
      if (valueA === undefined) return 1;
      if (valueB === undefined) return -1;

      // Your custom sorting logic for defined values
      return valueA.localeCompare(valueB);
    },
    maxSize: 130,
  }),
  columnHelper.accessor('software', {
    header: 'Humphree license',
    cell: (info) => <StatusPill fixedSize color={GetLicenseColor(info.getValue()) as any} text={info.getValue()} />,
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (info) => (
      <StatusPill color={GetStatusColor(info.getValue()) as any} text={GetStatusText(info.getValue())} fixedSize />
    ),
    enableGlobalFilter: false,
  }),
];
